import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Plus, Printer, Edit, Check, X, Send, Lightbulb, Loader2, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PrinterButton from "../../IndentComponents/PrinterButton";
import { IndentPDF } from "../../../../components/Header/IndentPDF";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div
          ref={tooltipRef}
          className="absolute z-50 p-2 bg-gray-800 text-white text-sm rounded shadow-lg max-w-xs whitespace-normal -top-2 left-full ml-2"
        >
          {content}
        </div>
      )}
    </div>
  );
};

const SOSHItemsTable = ({
  indentID,
  calculateTotalAmount,
  isDraft,
  isEditable = true,
  onSubmit,
  onPrint,
  onCloseIndent,
  onIndentStatusChange,
  onForceClosureStatusChange,
  forceClosureStatus,
  userId,
  isClosed = false,
  indentStatus = { l1a: 0, l1: 0, l2: 0, po: 0, ph: 0, ir: 0, sh: 1 },
}) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [editModes, setEditModes] = useState({});
  const [tempQuantities, setTempQuantities] = useState({});
  const [tempRemarks, setTempRemarks] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [itemDescriptions, setItemDescriptions] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState({});
  const [editingDate, setEditingDate] = useState(null);
  const fetchedDescriptionsRef = useRef(new Set());
  const [showDialog, setShowDialog] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState({});
  const isAdmin = useSelector((state) => state.auth.role === 'admin');

  const formatDecimal = (value) => {
  return Math.round(parseFloat(value || 0));
  };

  const fetchIndentData = async (signal) => {
    try {
      const indentResponse = await axios.get(
        `${API_BASE_URL}/l2page/indents/${indentID}`,
        { signal }
      );
      const indentData = indentResponse.data;
      const indentDetails = Array.isArray(indentData) ? indentData[0] : indentData;

      const itemsResponse = await axios.get(
        `${API_BASE_URL}/l2page/indentitems/${indentID}`,
        { signal }
      );
      const itemsData = itemsResponse.data;

      return {
        indentId: indentID,
        creationDate: indentDetails?.CreationDate ||
          new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
        projectName: indentDetails?.ProjectName || "N/A",
        userName: indentDetails?.UserName || "N/A",
        taskScheduled: indentDetails?.TaskSchedule || "N/A",
        status: indentDetails?.Status || "Active",
        invoicingType: indentDetails?.TypeName || "N/A",
        items: itemsData.map((item) => ({
          name: item.ItemName,
          category: item.CategoryName,
          au: item.UnitName,
          quantity: item.Quantity,
          quantityReceived: item.QuantityReceived,
          difference: item.Difference,
          remarks: item.Remarks,
          stock: item.Stock,
          description: item.Description || "__",
          promisedDays: item.NormalDays,
          promisedDays2: item.EmergencyDays,
          committedDate: item.CommitedDate,
          so: item.IR,
          ph: item.PH,
        })),
      };
    } catch (error) {
      if (error.name !== 'CanceledError') {
      console.error("Error fetching indent data:", error);
      }
      return null;
    }
  };

  const getPDFComponent = (data) => {
    return <IndentPDF data={data} />;
  };

  const fetchItemDescription = useCallback(async (itemId, signal) => {
    if (loadingDescriptions[itemId] || fetchedDescriptionsRef.current.has(itemId)) return;

    console.log("Fetching description for item:", itemId);
    setLoadingDescriptions((prev) => ({ ...prev, [itemId]: true }));
    fetchedDescriptionsRef.current.add(itemId);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/itemdescription/${itemId}`,
        { params: { indentId: indentID }, signal }
      );

      console.log("Description API response:", response.data);

      if (response.data && response.data.Description) {
        setItemDescriptions((prev) => ({
          ...prev,
          [itemId]: response.data.Description,
        }));
      } else {
        setItemDescriptions((prev) => ({
          ...prev,
          [itemId]: "",
        }));
      }
    } catch (error) {
      if (error.name !== 'CanceledError') {
      console.error("Error fetching description:", error);
      }
      setItemDescriptions((prev) => ({
        ...prev,
        [itemId]: "",
      }));
    } finally {
      setLoadingDescriptions((prev) => ({ ...prev, [itemId]: false }));
    }
  }, [indentID]);

  const fetchItemRemarks = async (signal, itemId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/shpage/indentitems`,
        {
          params: { indentId: indentID },
          signal,
        }
      );
      
      const item = response.data.find(item => item.IndentItemID === itemId);
      if (item && item.Remarks) {
        setRemarks(item.Remarks);
      } else {
        setRemarks("");
      }
    } catch (error) {
      if (error.name !== 'CanceledError') {
      console.error("Error fetching remarks:", error);
      }
      setError("Failed to load remarks");
    }
  };

  const fetchIndentItems = async (signal) => {
    if (!indentID) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/shpage/indentitems`,
        {
          params: { indentId: indentID },
          signal,
        }
      );

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => {
          const formattedDate = item.CommitedDate
            ? new Date(item.CommitedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })
            : null;

          return {
            indentItemID: item.IndentItemID,
            itemName: item.ItemName,
            quantity: formatDecimal(item.Quantity),
            quantityReceived: formatDecimal(item.QuantityReceived || 0),
            difference: formatDecimal(Number(item.Quantity) - Number(item.QuantityReceived || 0)),
            stock: item.Stock || 0,
            categoryID: item.CategoryID,
            itemID: item.ItemID,
            remarks: item.Remarks,
            remarks2: item.Description,
            promisedDate: item.PromisedDate,
            commitedDate: formattedDate,
            accountingUnitID: item.AccountingUnitID,
            unitname: item.UnitName,
            forceClosure: item.ForceClosure || 0,
            isForceClosed: Boolean(item.ForceClosure),
          };
        });

      setItems(fetchedItems);

      // Check if all items with differences are force closed
      const itemsWithDifference = fetchedItems.filter(item => item.difference !== 0);
      const allForceClosed = itemsWithDifference.length > 0 && itemsWithDifference.every(item => item.isForceClosed);
      onForceClosureStatusChange && onForceClosureStatusChange(allForceClosed);
    } catch (error) {
      if (error.name !== 'CanceledError') {
      console.error("Error fetching indent items:", error);
      setError("Failed to load indent items");
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchIndentItems(controller.signal);
    // Clear the fetched descriptions ref when indentID changes
    fetchedDescriptionsRef.current.clear();
    return () => controller.abort();
  }, [indentID]);

  useEffect(() => {
    if (items.length > 0) {
      const controller = new AbortController();
      items.forEach((item) => {
        if (item.itemID && !fetchedDescriptionsRef.current.has(item.itemID) && !loadingDescriptions[item.itemID]) {
          fetchItemDescription(item.itemID, controller.signal);
        }
      });
      return () => controller.abort();
    }
  }, [items, fetchItemDescription]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      fetchedDescriptionsRef.current.clear();
    };
  }, []);

  const handleQuantityReceived = async (index, quantityReceived, remarks2) => {
    const currentItem = items[index];
    const safeQuantityReceived = quantityReceived === "" ? 0 : formatDecimal(quantityReceived);
    const difference = formatDecimal(currentItem.quantity - safeQuantityReceived);

    try {
      await axios.put(
        `${API_BASE_URL}/irpage/indentitems/${currentItem.indentItemID}`,
        {
          QuantityReceived: safeQuantityReceived,
          Difference: difference,
          Remarks: remarks2
        }
      );

      const updatedItems = [...items];
      updatedItems[index] = {
        ...updatedItems[index],
        quantityReceived: safeQuantityReceived,
        difference,
        remarks2: remarks2
      };

      setItems(updatedItems);
      setEditModes((prev) => ({ ...prev, [currentItem.indentItemID]: false }));
    } catch (error) {
      console.error("Detailed Error in Quantity Update:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update received quantity"
      );
    }
  };

  const handleForceClose = async () => {
    if (!selectedItemId || !remarks.trim()) return;
    
    setLoading(prev => ({ ...prev, [selectedItemId]: true }));
    
    try {
      if (isEditing) {
        // Update existing remarks
        await axios.put(`${API_BASE_URL}/shpage/indentitems/remarks/${selectedItemId}`, {
          remarks: remarks.trim(),
          userId: userId
        });
      } else {
        // Create new force closure
        await axios.post(`${API_BASE_URL}/shpage/indentitems/forceClose`, {
          indentItemId: selectedItemId,
          userId: userId,
          remarks: remarks.trim()
        });
      }
      
      await fetchIndentItems();
      setShowDialog(false);
      setSelectedItemId(null);
      setRemarks("");
      setIsEditing(false);

      // Check if all items with differences are force closed
      const itemsWithDifference = items.filter(item => item.difference !== 0);
      const allForceClosed = itemsWithDifference.length > 0 && 
                            itemsWithDifference.every(item => item.isForceClosed);
      
      if (allForceClosed) {
        onForceClosureStatusChange && onForceClosureStatusChange(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         "Failed to process request";
      setError(errorMessage);
      console.error("Force Close/Update Error:", error.response?.data);
    } finally {
      setLoading(prev => ({ ...prev, [selectedItemId]: false }));
    }
  };

  const handleForceCloseIndent = async () => {
    try {
      await axios.post(`${API_BASE_URL}/shpage/indents/forceClose`, {
        indentId: indentID,
        userId: userId
      });

      navigate('/SOSHPage');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         "Failed to force close indent";
      setError(errorMessage);
      console.error("Force Indent Closure Error:", error.response?.data);
    }
  };

  const canForceCloseIndent = () => {
    const itemsWithDifference = items.filter(item => item.difference !== 0);
    const allForceClosed = itemsWithDifference.length > 0 && 
                          itemsWithDifference.every(item => item.isForceClosed);
    return allForceClosed;
  };

  const toggleEditMode = (itemId, currentQuantity, currentRemarks) => {
    setEditModes((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));

    if (!editModes[itemId]) {
      setTempQuantities((prev) => ({
        ...prev,
        [itemId]: currentQuantity,
      }));
      setTempRemarks((prev) => ({
        ...prev,
        [itemId]: currentRemarks || "",
      }));
    }
  };

  const handleQuantityChange = (itemId, value) => {
    setTempQuantities((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const handleRemarksChange = (itemId, value) => {
    setTempRemarks((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const handleCloseIndent = async () => {
    try {
      await axios.post(`${API_BASE_URL}/irpage/indents/close`, {
        indentId: indentID,
        userId: userId,
        forceClose: false,
      });

      navigate("/SOSHPage");
      onCloseIndent && onCloseIndent();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to close indent";
      setError(errorMessage);
      console.error("Indent Closure Error:", error.response?.data);
    }
  };

  const handleSendToStoreHead = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/irpage/indents/sendToStoreHead`,
        {
          IndentID: indentID,
          UserID: userId,
        }
      );

      if (response.status === 200 && response.data.status === "success") {
        const newStatus = {
          ...indentStatus,
          sh: 0,
        };
        onIndentStatusChange && onIndentStatusChange(newStatus);

        navigate("/SOSHPage", {
          state: { message: "Successfully sent to Store Head for approval" },
        });
      } else {
        throw new Error(response.data.error || "Unexpected server response");
      }
    } catch (error) {
      let errorMessage;
      if (error.response) {
        errorMessage =
          error.response.data.error || "Failed to send to store head";
      } else if (error.request) {
        errorMessage = "Network error: Please check your connection";
      } else {
        errorMessage = error.message || "An unexpected error occurred";
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = () => {
    navigate("/SOSHPage");
  };

  const handleDateUpdate = async (itemId, newDate) => {
    try {
      await axios.put(
        `${API_BASE_URL}/viewTable/indentitems/committed-date/${itemId}`,
        { CommitedDate: newDate },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      await fetchIndentItems();
    } catch (error) {
      console.error('Error updating committed date:', error);
      setError('Failed to update committed date');
    }
  };

  const renderDescriptionCell = (itemId) => {
    if (loadingDescriptions[itemId]) {
      return (
        <div className="w-[18px] h-[18px] mx-auto flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-blue-500" />
        </div>
      );
    }

    const description = itemDescriptions[itemId];
    return (
      <div className="flex items-center space-x-2">
        <Tooltip content={description || "No description available"}>
          <button
            className={`${
              description
                ? "text-yellow-500 hover:text-yellow-600"
                : "text-gray-400 hover:text-gray-600"
            } transition-colors duration-200`}
            onClick={() => {
              if (!description && !loadingDescriptions[itemId]) {
                fetchItemDescription(itemId);
              }
            }}
          >
            <Lightbulb size={18} />
          </button>
        </Tooltip>
      </div>
    );
  };

  const renderDateCell = (item) => {
    if (editingDate?.itemId === item.itemID) {
      return (
        <div className="flex items-center space-x-2">
          <input
            type="date"
            className="border rounded px-2 py-1 text-sm"
            value={editingDate.date}
            onChange={(e) => setEditingDate(prev => ({ ...prev, date: e.target.value }))}
          />
          <button
            className="text-green-500 hover:text-green-700"
            onClick={() => {
              handleDateUpdate(item.indentItemID, editingDate.date);
              setEditingDate(null);
            }}
          >
            <Check size={16} />
          </button>
          <button
            className="text-red-500 hover:text-red-700"
            onClick={() => setEditingDate(null)}
          >
            <X size={16} />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2">
        <span>{item.commitedDate}</span>
        {isAdmin && (
          <button
            className="text-blue-500 hover:text-blue-700"
            onClick={() => setEditingDate({ itemId: item.itemID, date: item.commitedDate })}
          >
            <Edit size={16} />
          </button>
        )}
      </div>
    );
  };

  const shouldDisableActions = isClosed || forceClosureStatus || indentStatus.sh === 0;
  const allDifferencesZero = items.every((item) => item.difference === 0);

  return (
    <div className="bg-white shadow-md rounded-lg px-4 pb-6 border border-gray-200">
      {(isClosed || forceClosureStatus || indentStatus.sh === 0) && (
        <div
          className={`
            ${
              forceClosureStatus === true
                ? "bg-yellow-100 border-yellow-400 text-yellow-700"
                : indentStatus.sh === 0
                ? "bg-blue-100 border-blue-400 text-blue-700"
                : "bg-green-100 border-green-400 text-green-700"
            }
            px-4 py-3 rounded mb-4
          `}
        >
          {forceClosureStatus === true
            ? "This Indent was Force Closed"
            : indentStatus.sh === 0
            ? "Sent for approval to Store Head"
            : "This Indent has been Closed"}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">Indent Items</h2>
        <PrinterButton
          getPDFComponent={getPDFComponent}
          filename={`indent-${indentID}`}
          fetchData={fetchIndentData}
          onError={(error) => setError("Failed to generate PDF. Please try again.")}
        />
      </div>

      {/* Responsive table container */}
      <div className="relative">
        <div className="overflow-x-auto" style={{ minWidth: '100%' }}>
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
            <tr>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      S.No
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                      Item Name
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                      Info
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      AU
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      Qty
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Qty Rec
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      Diff
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      Stock
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Committed
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                      Remarks & Actions
                    </th>
            </tr>
          </thead>
                <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item, index) => (
              <tr
                key={index}
                      className={`hover:bg-gray-50 transition-colors duration-200 ${
                  item.isForceClosed ? 'bg-yellow-50' : ''
                }`}
              >
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-2 py-3 text-sm font-medium text-gray-900 min-w-[150px]">
                        <div className="truncate max-w-[140px]" title={item.itemName}>
                          {item.itemName}
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-center">
                  {renderDescriptionCell(item.itemID)}
                </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900">
                        {item.unitname}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {Math.round(item.quantity)}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-center">
                  {shouldDisableActions ? (
                          <div className="flex items-center justify-center space-x-1">
                            <span className="text-sm text-gray-900">{Math.round(item.quantityReceived)}</span>
                      <Tooltip content={item.remarks || "No remarks available"}>
                              <Lightbulb size={14} className="text-yellow-500" />
                      </Tooltip>
                    </div>
                  ) : (
                    <>
                      {editModes[item.indentItemID] ? (
                              <div className="flex flex-col space-y-1">
                                <div className="flex items-center justify-center space-x-1">
                            <input
                              type="number"
                              step="1"
                                    className="w-16 border rounded px-1 py-1 text-center text-xs"
                              value={tempQuantities[item.indentItemID] || ""}
                              onChange={(e) => {
                                const value = e.target.value === "" ? 0 : parseFloat(e.target.value);
                                handleQuantityChange(item.indentItemID, value);
                              }}
                              min="0"
                            />
                            <button
                              className="text-green-500 hover:bg-green-100 rounded-full p-1"
                              onClick={() => handleQuantityReceived(
                                index,
                                tempQuantities[item.indentItemID],
                                tempRemarks[item.indentItemID]
                              )}
                            >
                                    <Check size={14} />
                            </button>
                            <button
                              className="text-red-500 hover:bg-red-100 rounded-full p-1"
                              onClick={() => toggleEditMode(item.indentItemID)}
                            >
                                    <X size={14} />
                            </button>
                          </div>
                          <input
                            type="text"
                                  className="w-full border rounded px-1 py-1 text-xs"
                                  placeholder="Remarks"
                            value={tempRemarks[item.indentItemID] || ""}
                            onChange={(e) => handleRemarksChange(item.indentItemID, e.target.value)}
                          />
                        </div>
                      ) : (
                              <div className="flex items-center justify-center space-x-1">
                                <span className="text-sm text-gray-900">{Math.round(item.quantityReceived)}</span>
                          <Tooltip content={item.remarks || "No remarks available"}>
                                  <Lightbulb size={14} className="text-yellow-500" />
                          </Tooltip>
                          <button
                            className="text-blue-500 hover:bg-blue-100 rounded-full p-1"
                            onClick={() => toggleEditMode(
                              item.indentItemID,
                              item.quantityReceived,
                              item.remarks
                            )}
                          >
                                  <Edit size={14} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {item.difference}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {item.stock}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-center">
                  {renderDateCell(item)}
                </td>
                      <td className="px-2 py-3 min-w-[200px]">
                  {/* Combined Remarks & Actions */}
                  {item.difference !== 0 ? (
                    item.isForceClosed ? (
                            <div className="flex items-center space-x-1">
                              <span className="text-yellow-600 font-medium text-xs truncate max-w-[150px]" title={item.remarks || "Force Closed"}>
                                {item.remarks || "Force Closed"}
                              </span>
                        <Tooltip content={item.remarks || "No remarks available"}>
                                <Lightbulb size={14} className="text-yellow-500 flex-shrink-0" />
                        </Tooltip>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedItemId(item.indentItemID);
                          setIsEditing(false);
                          setRemarks("");
                          setShowDialog(true);
                        }}
                        disabled={loading[item.indentItemID]}
                        className={`
                          ${loading[item.indentItemID] 
                            ? 'bg-yellow-400 cursor-wait' 
                            : 'bg-yellow-500 hover:bg-yellow-600'
                          } 
                                text-white px-2 py-1 rounded text-xs transition-colors duration-200 whitespace-nowrap
                        `}
                      >
                              {loading[item.indentItemID] ? 'Processing...' : 'Force Close'}
                      </button>
                    )
                  ) : (
                          <span className="text-green-600 font-medium text-xs">
                      No Difference
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
            </div>
          </div>
        </div>
      </div>

      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {isEditing ? 'Edit Remarks' : 'Add Remarks for Force Closure'}
              </h3>
              <button 
                onClick={() => {
                  setShowDialog(false);
                  setSelectedItemId(null);
                  setRemarks("");
                  setIsEditing(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              placeholder="Enter remarks..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full min-h-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowDialog(false);
                  setSelectedItemId(null);
                  setRemarks("");
                  setIsEditing(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleForceClose}
                disabled={!remarks.trim()}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed"
              >
                {isEditing ? 'Update Remarks' : 'Force Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {!shouldDisableActions && (
          <>
            <button
              onClick={handleSaveDraft}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm"
            >
              Save Draft
            </button>
            {allDifferencesZero ? (
              <button
                onClick={handleCloseIndent}
                disabled={isSubmitting}
                className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm ${
                  isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Close Indent
              </button>
            ) : (
              <button
                onClick={handleSendToStoreHead}
                disabled={isSubmitting}
                className={`bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center text-sm ${
                  isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Send className="mr-2" size={16} />
                {isSubmitting ? "Sending..." : "Send to Store Head"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SOSHItemsTable; 